import React from "react";
import {
    AppBar,
    Toolbar,
    IconButton,
    Avatar,
    Menu,
    MenuItem,
    Box,
    Typography,
    Divider,
    Tooltip,
    useTheme,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import KeyboardIcon from "@mui/icons-material/KeyboardOutlined";
import { useNavigate } from "react-router-dom";
import { useDarkMode } from "../../context/DarkModeContext";
import DarkModeToggle from "../global/DarkModeToggle";
import { TransLang } from "./TransLang";
import SettingsButton from "../settings/drawer/SettingsButton";
import ShortcutsHelpDialog from "../global/ShortcutsHelpDialog";
import { useAuthContext } from "../../auth/useAuthContext";
import toast from "react-hot-toast";
import i18next from "i18next";

export default function Navbar() {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const { toggleDarkMode } = useDarkMode();
    const navigate = useNavigate();
    const profile = JSON.parse(localStorage.getItem("user"));
    let lng = i18next.language;

    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const [shortcutsOpen, setShortcutsOpen] = React.useState(false);
    const isArabic = (lng || "en").startsWith("ar");

    const handleOpen = (e) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const { logout } = useAuthContext();
    const handleLogout = async () => {
        try {
            const response = await logout();
            const message = response?.data?.message || "Logged out successfully";
            localStorage.removeItem("user");
            localStorage.removeItem("token");
            toast.success(message);
            navigate("/login");
        } catch (error) {
            console.error(error);
            const errorMessage =
                error?.response?.data?.message || error?.message || "Logout failed";
            toast.error(errorMessage);
        }
    };

    return (
        <AppBar
            position="static"
            elevation={0}
            sx={{
                borderRadius: 0,
                backgroundColor: theme.palette.primary.main,
                backgroundImage: isDark
                    ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                    : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                color: "#FFFFFF",
                // Subtle drop shadow tinted by the active preset's primary color.
                boxShadow: `0 2px 10px 0 ${theme.palette.primary.main}59`,
            }}
        >
            <Toolbar
                sx={{
                    minHeight: 60,
                    px: 2.5,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1.5,
                }}>
                {/* تغيير اللغة + الثيم + الإعدادات */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <TransLang />
                    <Box
                        onClick={toggleDarkMode}
                        sx={{
                            color: "#FFFBEB",
                            "&:hover": { bgcolor: "rgba(15,23,42,0.12)" },
                        }}
                    >
                        <DarkModeToggle />
                    </Box>
                    <SettingsButton />

                    <Tooltip
                        title={isArabic ? "اختصارات لوحة المفاتيح (?)" : "Keyboard shortcuts (?)"}
                    >
                        <IconButton
                            onClick={() => setShortcutsOpen(true)}
                            size="small"
                            sx={{
                                color: "#FFFBEB",
                                "&:hover": { bgcolor: "rgba(15,23,42,0.12)" },
                            }}
                        >
                            <KeyboardIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Box sx={{ ml: 1 }}>
                    <IconButton
                        onClick={handleOpen}
                        size="small"
                        sx={{
                            p: 0.3,
                            borderRadius: 999,
                            border: "1px solid rgba(15,23,42,0.25)",
                            bgcolor: "rgba(15,23,42,0.08)",
                            "&:hover": {
                                bgcolor: "rgba(15,23,42,0.16)",
                            },
                        }}
                    >
                        <Avatar
                            src={profile?.image?.thumb}
                            sx={{ width: 32, height: 32 }}
                        />
                    </IconButton>

                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        PaperProps={{
                            sx: {
                                mt: 1,
                                minWidth: 220,
                                borderRadius: 2,
                                boxShadow:
                                    "0 10px 25px rgba(15,23,42,0.25)", // شادو واضح للقائمة
                            },
                        }}
                    >
                        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {profile?.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {profile?.email}
                            </Typography>
                        </Box>
                        <Divider sx={{ my: 0.5 }} />
                        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                            <Typography variant="body2" color="text.secondary" onClick={() => navigate('/profile')} sx={{ cursor: 'pointer' }}>
                                {lng === "ar" ? "عرض الملف الشخصي" : "View Profile"}
                            </Typography>
                        </Box>
                        <Divider sx={{ my: 0.5 }} />
                        <MenuItem
                            onClick={() => {
                                handleLogout();
                                handleClose();
                            }}
                            sx={{
                                py: 1,
                            }}
                        >
                            <LogoutIcon
                                fontSize="small"
                                sx={{ mr: 1, color: theme.palette.error.main }}
                            />
                            <Typography variant="body2">
                                {lng === "ar" ? "تسجيل الخروج" : "Logout"}
                            </Typography>
                        </MenuItem>
                    </Menu>
                </Box>
            </Toolbar>

            <ShortcutsHelpDialog
                open={shortcutsOpen}
                onClose={() => setShortcutsOpen(false)}
            />
        </AppBar>
    );
}
